import { useState, useCallback, memo } from "react";
import ControlPanel from "./ControlPanel";
import './bible.css';
import Drawing from "./drawing/Drawing";
import logo from '../../img/logo.jpeg';
import { useNavigate } from "react-router-dom";

/**
 * Utility: Splits text into [word, punctuation] pairs.
 * e.g. "God," → { word: "God", punct: "," }
 */
const splitWordAndPunct = (token) => {
    const match = token.match(/^([A-Za-z0-9’'’-]+)([^A-Za-z0-9’'’-]*)$/);
    if (!match) return { word: token, punct: '' };
    return { word: match[1], punct: match[2] };
};

/**
 * Renders a single word (without punctuation). Handles single-click highlighting.
 * MEMOIZED for performance.
 */
const WordSpan = memo(({ word, punct, verseIndex, wordIndex, isHighlighted, toggleWordHighlight }) => {
    return (
        <>
            <span
                className={`word-span ${isHighlighted ? 'highlighted-word' : ''}`}
                onClick={(e) => {
                    e.stopPropagation(); // Prevent verse click
                    toggleWordHighlight(verseIndex, wordIndex);
                }}
            >
                {word}
            </span>
            {punct}
            {' '}
        </>
    );
});

WordSpan.displayName = "WordSpan";

/**
 * Renders a single verse. Handles highlight via button click and renders words individually.
 * MEMOIZED to reduce re-renders when other verses update.
 */
const VerseBox = memo(({ verse, verseIndex, chapterRefPrefix, highlightedState, setHighlightedState }) => {
    const rawTokens = verse.text.match(/\S+/g) || [];
    const tokenPairs = rawTokens.map(splitWordAndPunct);
    const isVerseHighlighted = highlightedState[`v${verseIndex}_verse`] === true;
    const fullReference = `${verse.verseNumber}`;

    const toggleWordHighlight = useCallback((vIndex, wIndex) => {
        const key = `v${vIndex}_w${wIndex}`;
        setHighlightedState(prev => {
            // Use a shallow clone to minimize re-render costs
            if (prev[key]) {
                const { [key]: _, ...rest } = prev;
                return rest;
            } else {
                return { ...prev, [key]: true };
            }
        });
    }, [setHighlightedState]);

    const toggleVerseHighlight = useCallback((e) => {
        // Stop propagation to prevent any parent element from reacting to the button click
        e.stopPropagation(); 
        const key = `v${verseIndex}_verse`;
        setHighlightedState(prev => {
            if (prev[key]) {
                const { [key]: _, ...rest } = prev;
                return rest;
            } else {
                return { ...prev, [key]: true };
            }
        });
    }, [verseIndex, setHighlightedState]);

    return (
        // Wrapper for running text style. Removed onDoubleClick.
        <span
            className={`verse-content-wrapper ${isVerseHighlighted ? 'highlighted-verse' : ''}`}
            // Note: onDoubleClick is removed as requested
        >
            {/* NEW: Highlight Button visible on hover */}
            <button
                className={`verse-highlight-button ${isVerseHighlighted ? 'active' : ''}`}
                onClick={toggleVerseHighlight}
                title={isVerseHighlighted ? 'Remove Verse Highlight' : 'Highlight Verse'}
            >
                {/* Using a simple text for the button, you can replace with an icon */}
                {isVerseHighlighted ? '➖' : '✨'}
            </button>

            {/* Verse Number */}
            <span className="verse-number">
                {fullReference}
            </span>

            {/* Verse Text Content */}
            <span className="verse-text-content">
                {tokenPairs.map((pair, wordIndex) => (
                    <WordSpan
                        key={wordIndex}
                        word={pair.word}
                        punct={pair.punct}
                        verseIndex={verseIndex}
                        wordIndex={wordIndex}
                        isHighlighted={highlightedState[`v${verseIndex}_w${wordIndex}`] === true}
                        toggleWordHighlight={toggleWordHighlight}
                    />
                ))}
            </span>
            {' '}
        </span>
    );
});

VerseBox.displayName = "VerseBox";

const Bible = () => {
    const navigate = useNavigate();

    const [verseData, setVerseData] = useState([]);
    const [currentRef, setCurrentRef] = useState('Select a Book and Chapter, then press GO.');
    const [drawMode, setDrawMode] = useState(false);
    const [highlightedState, setHighlightedState] = useState({});
    const chapterRefPrefix = currentRef.split(':')[0];
    const [customCallBack, setCustomCallback] = useState(null);
    /** Clears all highlights (both words and verses) */
    const clearAllHighlights = useCallback(() => {
        setHighlightedState({});
    }, []);

    return (
        <div className="bible-app-container">
            <header className="app-header row align-items-center justify-content-between d-flex">
                <div className="bibleLogo">
                    <img
                        src={logo}
                        alt="logo"
                        title="Alkapoor Baptist Church"
                        className='cursor-pointer'
                        draggable="false"
                        onClick={() => {
                            if (window.location.pathname !== '/') {
                                navigate('/');
                            }
                        }}
                    />
                </div>
                <h1>Bible App (NIV)</h1>
            </header>

            <main className="verse-display-area">
                <Drawing {...{ drawMode, setCustomCallback }} />
                <h2 className="content-header">{currentRef}</h2>
                {/* Wrapped all verses in a single container for running text flow */}
                <div className="running-text-container"> 
                    {verseData.length > 0 ? (
                        verseData.map((verse, index) => (
                            <VerseBox
                                key={index}
                                verse={verse}
                                verseIndex={index}
                                highlightedState={highlightedState}
                                setHighlightedState={setHighlightedState}
                                chapterRefPrefix={chapterRefPrefix}
                            />
                        ))
                    ) : (
                        <p className="initial-message">{currentRef}</p>
                    )}
                </div>
            </main>

            <ControlPanel
                {...{ customCallBack, setVerseData, setCurrentRef, setDrawMode, drawMode, clearAllHighlights }}
            />
        </div>
    );
};

export default Bible;